import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { RulesError } from './useRules'

/**
 * The log's embedded transaction as it is NOW (backend 521): enough for a row to show its date,
 * merchant and amount without reading the transaction by id.
 */
export type RuleLogTxn = {
  /** YYYY-MM-DD. */
  date: string
  merchantId: string | null
  /** `merchant_display_name`; null when the transaction has none. */
  merchant: string | null
  /** Integer cents in DISPLAY sign (an expense is negative), in `currency`. */
  amountCents: number
  currency: string
}

/** One application of a rule to one transaction, still in effect. A transaction
 *  the rule changed in two runs appears twice: each is its own row. */
export type RuleLog = {
  id: string
  transactionId: string
  /** The log's snapshot of the merchant text, for a row whose transaction cannot be read. */
  merchantText: string | null
  /** An instant (ISO 8601 with zone). */
  appliedAt: string
  batchId: string
  /** The wire's open objects; `changeLines` reads them. */
  changes: unknown[]
  /** `null`: the transaction is no longer available to this user (deleted, removed by the bank, or
   *  no longer theirs). The server always sends the field; an absent one reads the same. */
  txn: RuleLogTxn | null
}
export type RuleLogPage = { logs: RuleLog[]; total: number }

/** Rows per request. The server allows 1–100. */
export const LOG_PAGE_SIZE = 25

type LogList = components['schemas']['RuleApplicationLogList']

const toLogTxn = (t: components['schemas']['RuleLogTransaction']): RuleLogTxn => ({
  date: t.date,
  merchantId: t.merchant_id,
  merchant: t.merchant_display_name,
  // A decimal STRING, expense-positive in storage; parsed once here and re-signed for display.
  amountCents: signedTransactionCents(parseMoney(t.amount)),
  currency: t.iso_currency_code,
})

const toLog = (l: components['schemas']['RuleApplicationLogEntry']): RuleLog => ({
  id: l.id,
  transactionId: l.transaction_id,
  merchantText: l.transaction_merchant,
  appliedAt: l.applied_at,
  batchId: l.batch_id,
  changes: l.changes,
  txn: l.transaction === undefined || l.transaction === null ? null : toLogTxn(l.transaction),
})

/** Pages → one list, each log once: offset paging can repeat a row across a boundary when a new run lands mid-scroll. */
function flatten(data: InfiniteData<LogList, number>): RuleLogPage {
  const seen = new Set<string>()
  const logs: RuleLog[] = []
  for (const p of data.pages) {
    for (const l of p.logs) {
      if (seen.has(l.id)) continue
      seen.add(l.id)
      logs.push(toLog(l))
    }
  }
  return { logs, total: data.pages[0]?.total ?? 0 }
}

/**
 * `GET /rule-logs?rule_id=&is_reverted=false`, newest first and paged: what this rule has changed that
 * has not been undone. `total` is the count of those applications, which, unlike `times_applied`, goes
 * down when a run is undone.
 *
 * Under the `rules` root, so every rule write (save, delete, a batch Undo) refreshes it.
 */
export function useRuleLogs(ruleId: string): {
  state: AsyncState<RuleLogPage>
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
} {
  const result = useInfiniteQuery({
    queryKey: keys.rules.logs(ruleId),
    queryFn: async ({ pageParam, signal }): Promise<LogList> => {
      const { data, error, response } = await api.GET('/api/v1/rule-logs', {
        params: { query: { rule_id: ruleId, is_reverted: false, page: pageParam, page_size: LOG_PAGE_SIZE } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data
    },
    initialPageParam: 1,
    getNextPageParam: (last, _all, lastParam) => (last.has_more ? lastParam + 1 : undefined),
    select: flatten,
  })
  const state = fromQuery(result, {
    message: () => "This rule's changes could not be loaded.",
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
    isEmpty: (d) => d.logs.length === 0,
  })
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = result
  const loadMore = useCallback(() => {
    // Guarded here: a button pressed twice must not ask for the same page twice.
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  return { state, hasMore: hasNextPage, loadingMore: isFetchingNextPage, loadMore }
}

/**
 * Whether this rule has EVER changed a transaction, undone or not: one row of its unfiltered log.
 * Asked only when nothing is in effect (`enabled`), to tell "Not yet" from "All changes undone".
 * `undefined` while unknown or after a failure, so the caller says neither.
 */
export function useRuleEverApplied(ruleId: string, enabled: boolean): boolean | undefined {
  const query = useQuery({
    queryKey: keys.rules.logsEver(ruleId),
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/v1/rule-logs', {
        params: { query: { rule_id: ruleId, page: 1, page_size: 1 } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data.total > 0
    },
    enabled,
  })
  return query.data
}

/** The rule that set a field, named from its log. `ruleId` is null once the rule is deleted (the log
 *  keeps `rule_name` as a snapshot), so there is nothing to link to. */
export type SetByRule = { ruleId: string | null; name: string }

/**
 * Whether a log entry's `changes` wrote `field` and it still stands. The objects are open on the wire.
 * A partial revert leaves the log open (`is_reverted: false`) but stamps each change it undid with
 * `reverted_at`, and puts back the field's previous source, which may be an older rule's: such a
 * change is no longer what set the field (rv-review M1).
 */
const writes = (changes: unknown[], field: string) =>
  changes.some((c) => {
    if (typeof c !== 'object' || c === null) return false
    const r = c as Record<string, unknown>
    return r.field === field && (r.reverted_at === undefined || r.reverted_at === null || r.reverted_at === '')
  })

/**
 * `GET /rule-logs?transaction_id=&is_reverted=false`, newest first: the newest entry whose `changes`
 * name `field` is the rule that set it. Asked only when the row says a rule set it (`enabled`).
 *
 * - `undefined` while unknown or after a failure, `null` when no log names the field: an empty
 *   answer is also what a household member gets on the owner's transaction. Either way the caller
 *   keeps its generic words.
 */
export function useRuleThatSet(transactionId: string, field: string, enabled: boolean): SetByRule | null | undefined {
  // Stable for one field, so `select`'s result is kept between renders and a caller may memo on it.
  const pick = useCallback(
    (logs: components['schemas']['RuleApplicationLogEntry'][]): SetByRule | null => {
      const hit = logs.find((l) => writes(l.changes, field))
      return hit === undefined ? null : { ruleId: hit.rule_id, name: hit.rule_name }
    },
    [field],
  )
  const query = useQuery({
    queryKey: keys.rules.forTransaction(transactionId),
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/v1/rule-logs', {
        params: { query: { transaction_id: transactionId, is_reverted: false, page: 1, page_size: LOG_PAGE_LIMIT } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data.logs
    },
    select: pick,
    enabled,
  })
  return enabled ? query.data : undefined
}

/** The server's most per page: one transaction's whole history fits. */
const LOG_PAGE_LIMIT = 100
